import React, { useState } from 'react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import type { EjercicioAsignado, Recurso } from '../../types';
import { LADOS, MODALIDADES, VARIACIONES } from '../../data/constantes';
import { CampoArea, CampoNumero, CampoSelect, CampoTexto } from '../ui/Campo';
import { Boton } from '../ui/Boton';
import { Insignia } from '../ui/Etiquetas';
interface Props {
  ejercicio: EjercicioAsignado;
  recurso: Recurso | null;
  onCambiar: (parcial: Partial<EjercicioAsignado>) => void;
  onDuplicar: () => void;
  onQuitar: () => void;
  pedirLado: boolean;
}
export function EditorEjercicio({
  ejercicio,
  recurso,
  onCambiar,
  onDuplicar,
  onQuitar,
  pedirLado
}: Props) {
  const [abierto, setAbierto] = useState(!ejercicio.nombre);
  return <li className="rounded-xl border border-linea bg-lienzo p-3" data-id="element-28" data-mp="2hg0w7px8xy10">
      <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-29" data-mp="1jxr3cgl6xuyi">
        <div className="min-w-0" data-id="element-30" data-mp="1owtwb2nwcaq4">
          <p className="text-sm font-bold text-tinta-titulo" data-id="element-31" data-mp="1ow2csq7kq3g7">
            {ejercicio.nombre || 'Ejercicio personalizado sin nombre'}
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5" data-id="element-32" data-mp="1bo0vq4s2aynb">
            <Insignia data-id="element-33" data-mp="1boiylk98cb47">{ejercicio.minutos} min</Insignia>
            <Insignia tono="vino" data-id="element-34" data-mp="16wivjixv523e">{ejercicio.variacion}</Insignia>
            <Insignia data-id="element-35" data-mp="16uo73unduruk">{ejercicio.modalidad}</Insignia>
            <Insignia tono={pedirLado ? 'alerta' : 'neutro'} data-id="element-36" data-mp="11x66qsnflky6">Lado: {ejercicio.lado}</Insignia>
            {ejercicio.seriesRepes && <Insignia data-id="element-37" data-mp="xxnltwvm19vc">{ejercicio.seriesRepes}</Insignia>}
          </div>
          {pedirLado && <p className="mt-1 text-xs font-semibold text-alerta" data-id="element-38" data-mp="svy829h06i3k">
              Es una habilidad bilateral: indicá el lado a trabajar.
            </p>}
        </div>
        <div className="flex gap-1" data-id="element-39" data-mp="j0dduxi6em4d">
          <Boton variante="texto" tamano="sm" aria-expanded={abierto} onClick={() => setAbierto(v => !v)} data-id="element-40" data-mp="j169b2bopp77">
            <Pencil className="h-4 w-4" aria-hidden="true" data-id="element-41" data-mp="2dee8152s6ky4" />
            {abierto ? 'Cerrar' : 'Editar'}
          </Boton>
          <Boton variante="texto" tamano="sm" onClick={onDuplicar} data-id="element-42" data-mp="blubn9nfffs0">
            <Copy className="h-4 w-4" aria-hidden="true" data-id="element-43" data-mp="gffepg3vmzy2" />
            Duplicar
          </Boton>
          <Boton variante="texto" tamano="sm" onClick={onQuitar} data-id="element-44" data-mp="1r2f895kyyyt">
            <Trash2 className="h-4 w-4 text-alerta" aria-hidden="true" data-id="element-45" data-mp="6oknryforc6a" />
            Quitar
          </Boton>
        </div>
      </div>

      {abierto && <div className="mt-3 space-y-3 rounded-xl border border-linea bg-white p-3" data-id="element-46" data-mp="uf7ysskwehx2">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-47" data-mp="zczs9r16yc3d">
            <CampoTexto etiqueta="Nombre o código" value={ejercicio.nombre} onChange={e => onCambiar({
          nombre: e.target.value
        })} data-id="element-48" data-mp="lei823ahvhq7" />
            <CampoNumero etiqueta="Minutos" min={0} max={180} value={ejercicio.minutos} ayuda={recurso ? `Referencia del catálogo: ${recurso.duracionMin}–${recurso.duracionMax} min.` : undefined} onChange={e => onCambiar({
          minutos: Number(e.target.value)
        })} data-id="element-49" data-mp="3647ogkqujbl0" />
            <CampoTexto etiqueta="Series o repeticiones" value={ejercicio.seriesRepes} placeholder="Por ejemplo: 3 series de 5 repeticiones" onChange={e => onCambiar({
          seriesRepes: e.target.value
        })} data-id="element-50" data-mp="3cjy0zregxdof" />
            <CampoSelect etiqueta="Lado" value={ejercicio.lado} opciones={LADOS} onChange={e => onCambiar({
          lado: e.target.value as EjercicioAsignado['lado']
        })} data-id="element-51" data-mp="2jtg1f0cydufr" />
            <CampoSelect etiqueta="Modalidad" value={ejercicio.modalidad} opciones={MODALIDADES} onChange={e => onCambiar({
          modalidad: e.target.value as EjercicioAsignado['modalidad']
        })} data-id="element-52" data-mp="2ouww2e4ugknw" />
            <CampoSelect etiqueta="Variación" value={ejercicio.variacion} opciones={VARIACIONES} onChange={e => onCambiar({
          variacion: e.target.value as EjercicioAsignado['variacion']
        })} data-id="element-53" data-mp="zeun962w50ri" />
          </div>
          <CampoArea etiqueta="Notas" value={ejercicio.notas} onChange={e => onCambiar({
        notas: e.target.value
      })} data-id="element-54" data-mp="1d5pr23yj8ro8" />
          {recurso && <div className="rounded-xl border border-vino/20 bg-vino/5 p-3 text-sm text-tinta" data-id="element-55" data-mp="1n3x2k7a1yhiu">
              <p data-id="element-56" data-mp="1r5qxs01hatbm">
                <span className="font-semibold" data-id="element-57" data-mp="1r58skdwu94rk">Clave docente:</span> {recurso.claveDocente}
              </p>
              <p className="mt-1" data-id="element-58" data-mp="1w53jghgm5ucm">
                <span className="font-semibold" data-id="element-59" data-mp="1w429kgbc3dwh">Seguridad:</span> {recurso.seguridad}
              </p>
            </div>}
        </div>}
    </li>;
}